/**
 * P2.0 STEP C2 · K8 · Split di `lib/sortCompanies.js`.
 *
 * La parte CORE è stata assimilata in `foundation/formatting/sort.js`:
 *   - `italianCollator` (collator IT canonico Suite)
 *   - `sortByNameIt(list, options)` (funzione generica prodotto-agnostica)
 *
 * Questo file mantiene la parte SHARED PRODUCT (label "Tutte le aziende",
 * filtri per `bank_hours_enabled`, ecc.) e re-esporta l'`italianCollator`
 * dal CORE per retrocompatibilità dei consumatori esistenti.
 *
 * Le funzioni `sortCompaniesIt` e `sortCompaniesForSelector` diventano thin
 * wrapper su `sortByNameIt` (comportamento identico al pre-P2.0: pin in cima
 * dell'elemento con `id === "all"`).
 *
 * Superficie pubblica preservata al 100% per tutti i consumatori di
 * `@/lib/sortCompanies`.
 */

import { sortByNameIt } from "@/foundation/formatting";

/** Collator IT esposto per retrocompatibilità (destinazione canonica: Foundation). */
export { italianCollator } from "@/foundation/formatting";

/** Etichetta canonica per l'opzione "aggregato". */
export const ALL_COMPANIES_LABEL = "Tutte le aziende";
export const ALL_COMPANIES_ID = "all";

/**
 * Ordina una lista di aziende usando il collator italiano.
 * Se un elemento ha `id === "all"`, va sempre in prima posizione.
 *
 * @param {Array<{id:string, name:string}>} list
 * @returns {Array} Nuova lista ordinata (non muta l'input).
 */
export function sortCompaniesIt(list) {
  return sortByNameIt(list, { firstIdKey: "id", firstIdValue: ALL_COMPANIES_ID });
}

/**
 * Ordina una lista di aziende e — se richiesto — antepone la voce
 * `{ id: "all", name: "Tutte le aziende" }`. Usato dai selettori che
 * offrono all'utente la vista aggregata.
 *
 * @param {Array} list
 * @param {boolean} withAllOption  Se true, antepone la voce "Tutte le aziende".
 */
export function sortCompaniesForSelector(list, { withAllOption = false } = {}) {
  const sorted = sortCompaniesIt(list);
  if (!withAllOption) return sorted;
  return [{ id: ALL_COMPANIES_ID, name: ALL_COMPANIES_LABEL }, ...sorted];
}

/**
 * Filtro combinato per l'anagrafica aziende (v0.99.30-c2).
 * @param {Array} list
 * @param {{ query?: string, bankHours?: "all"|"on"|"off" }} opts
 */
export function filterCompanies(list, { query = "", bankHours = "all" } = {}) {
  let out = filterCompaniesByQuery(list, query);
  if (bankHours === "on") {
    out = out.filter((c) => c && c.bank_hours_enabled !== false);
  } else if (bankHours === "off") {
    out = out.filter((c) => c && c.bank_hours_enabled === false);
  }
  return out;
}

/**
 * Filtro testuale client-side per l'anagrafica aziende.
 * Match case-insensitive su: name, vat, tax_code, email.
 * Il parametro è "trimmed": stringa vuota → nessun filtro.
 */
export function filterCompaniesByQuery(list, query) {
  if (!Array.isArray(list)) return [];
  const q = (query || "").trim().toLowerCase();
  if (!q) return list;
  return list.filter((c) => {
    if (!c) return false;
    const parts = [c.name, c.vat, c.tax_code, c.email]
      .filter(Boolean)
      .map((s) => String(s).toLowerCase());
    return parts.some((p) => p.includes(q));
  });
}
